import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { OAuthProvider } from '../services/oauthService';
import { useAuth } from '../context/AuthContext';
import { config } from '../config/environment';

interface OAuthCallbackProps {
  provider: OAuthProvider;
}

export const OAuthCallback: React.FC<OAuthCallbackProps> = ({ provider }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setIsLoggedIn, setUser } = useAuth();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code') || '';
        const error = searchParams.get('error') || '';
        const errorDescription = searchParams.get('error_description') || '';

        console.group(`[OAuthCallback] ${provider} Callback Processing`);
        console.log('URL Search Params:');
        console.log('  - code:', code ? code.substring(0, 20) + '...' : '❌ MISSING');
        console.log('  - error:', error || '✓ None');
        console.log('  - error_description:', decodeURIComponent(errorDescription) || '✓ None');
        console.groupEnd();

        if (error) {
          throw new Error(`${error}: ${decodeURIComponent(errorDescription)}`);
        }

        if (!code) {
          throw new Error('No authorization code received from Google');
        }

        console.log(`[OAuthCallback] Processing ${provider} callback with code`);

        const apiUrl = config.apiUrl;
        console.log(`[OAuthCallback] API URL: ${apiUrl}`);

        let endpoint = '';

        if (provider === OAuthProvider.GOOGLE) {
          endpoint = '/auth/google/callback';
        } else if (provider === OAuthProvider.KAKAO) {
          endpoint = '/auth/kakao/callback';
        } else if (provider === OAuthProvider.NAVER) {
          endpoint = '/auth/naver/callback';
        }

        const fullUrl = `${apiUrl}${endpoint}`;
        
        console.group('[OAuthCallback] Sending request to backend');
        console.log('Endpoint:', fullUrl);
        console.log('Method:', 'POST');
        console.log('Headers:', {
          'Content-Type': 'application/json; charset=utf-8',
          'Accept': 'application/json; charset=utf-8',
        });
        console.log('Body:', { code: code.substring(0, 20) + '...' });
        console.groupEnd();

        const response = await axios.post(fullUrl, { code }, {
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'Accept': 'application/json; charset=utf-8',
          }
        });

        console.log(`[OAuthCallback] Backend response status: ${response.status}`);

        if (response.data.success) {
          // save-user 결과 확인
          if (!response.data.user || !response.data.user.id) {
            throw new Error('User save failed: Invalid user data from server');
          }

          console.log(`[OAuthCallback] ✅ ${provider} login successful`);
          
          // 토큰 저장
          localStorage.setItem('authToken', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          // userId 저장 (users 테이블의 UUID id)
          if (response.data.user.id) {
            localStorage.setItem('userId', String(response.data.user.id));
          }
          
          // AuthContext 업데이트
          setIsLoggedIn(true);
          setUser(response.data.user);
          
          console.log(`[OAuthCallback] User logged in:`, response.data.user.email);
          
          // 홈으로 리다이렉트
          setTimeout(() => {
            navigate('/');
            window.location.reload();
          }, 500);
        } else {
          throw new Error(response.data.error || 'Login failed');
        }
      } catch (error: any) {
        // 상세한 에러 로깅
        const errorDetails = {
          message: error.message,
          status: error.response?.status,
          responseData: error.response?.data,
          config: error.config ? {
            url: error.config.url,
            method: error.config.method,
            data: error.config.data
          } : null
        };
        
        console.group(`[OAuthCallback] ❌ ${provider} callback failed`);
        console.error('Error Details:', errorDetails);
        console.groupEnd();
        
        // 사용자 친화적인 에러 메시지
        let alertMessage = `${provider} 로그인에 실패했습니다.`;
        if (error.response?.status === 400) {
          alertMessage += ' (400 - 잘못된 요청 또는 redirect_uri 불일치)';
        } else if (error.response?.status === 401) {
          alertMessage += ' (401 - 인증 실패)';
        }
        
        alert(alertMessage);
        
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('userId');
        navigate('/');
      }
    };

    handleCallback();
  }, [provider, searchParams, navigate, setIsLoggedIn, setUser]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">로그인 중...</h1>
        <p className="text-gray-600">잠시만 기다려주세요.</p>
      </div>
    </div>
  );
};

export const KakaoCallback: React.FC = () => (
  <OAuthCallback provider={OAuthProvider.KAKAO} />
);

export const NaverCallback: React.FC = () => (
  <OAuthCallback provider={OAuthProvider.NAVER} />
);
